import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/AppContext";
import { createTimeLog } from "../../api/timeLog";

const AddTimeLogDialogWithoutTaskId = ({ open, onClose, task_id, onSuccess, tasks, projects, defaultLoggedDate }) => {
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [error, setError] = useState(null);
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedPhase, setSelectedPhase] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [form, setForm] = useState({
        task_id: task_id || "",
        logged_date: defaultLoggedDate || new Date().toISOString().split("T")[0],
        logged_minutes: "",
    });

    useEffect(() => {
        if (defaultLoggedDate) {
            setForm(prev => ({
                ...prev,
                logged_date: defaultLoggedDate,
            }));
        }
    }, [defaultLoggedDate]);

    const timeOptions = [...Array(18)].map((_, i) => {
        const hours = Math.floor((i + 1) * 30 / 60);
        const minutes = (i + 1) * 30 % 60;
        return {
            label: `${hours}:${minutes === 0 ? '00' : minutes}`,
            value: (i + 1) * 30,
        };
    });

    const selectedProjectObj = projects.find(p => p.id === parseInt(selectedProject));

    const filteredTasks = tasks?.filter(task =>
        (!selectedProject || task.project_id === parseInt(selectedProject)) &&
        (!selectedPhase || task.project_phase_id === parseInt(selectedPhase)) &&
        (!selectedCategory || task.project_category_id === parseInt(selectedCategory))
    ) || [];

    useEffect(() => {
        if (task_id) {
            setForm(prev => ({ ...prev, task_id }));
        }
    }, [task_id]);

    const handleSubmit = async () => {
        if (!form.logged_date || !form.logged_minutes) {
            setError(t("fields.all_required_error"));
            return;
        }

        setIsLoading(true);
        try {
            await createTimeLog(form);
            setError(null);
            onSuccess?.();
            setForm({ task_id: "", logged_date: defaultLoggedDate || new Date().toISOString().split("T")[0], logged_minutes: "" });
            setSelectedProject("");
            setSelectedPhase("");
            setSelectedCategory("");
            onClose();
        } catch (err) {
            console.error("Save error", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const commonSelectStyle = {
        '& .MuiInputBase-root': { backgroundColor: "transparent" },
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: "#999" },
            '&:hover fieldset': { borderColor: "#666" },
            '&.Mui-focused fieldset': { borderColor: "#333" },
        },
        '& .MuiInputBase-input': { color: "#000" },
        '& .MuiInputLabel-root': {
            color: "#666",
            "&.Mui-focused": { color: "#000" },
        },
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "#000" }}>{t("common.log_time")}</DialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important" }}>
                {error && (
                    <Box mb={2}>
                        <Box
                            sx={{
                                bgcolor: "#fdecea",
                                color: "#b71c1c",
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {t(`${error}`) || error}
                        </Box>
                    </Box>
                )}

                <Box sx={{ marginBottom: "16px" }}>
                    <TextField
                        fullWidth
                        label={t("fields.logged_date")}
                        type="date"
                        value={form.logged_date}
                        onChange={(e) => setForm({ ...form, logged_date: e.target.value })}
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={commonSelectStyle}
                    />
                </Box>

                <Box sx={{ marginBottom: "16px" }}>
                    <TextField
                        fullWidth
                        select
                        label={t("fields.logged_time")}
                        value={form.logged_minutes}
                        onChange={(e) => setForm({ ...form, logged_minutes: e.target.value })}
                        required
                        SelectProps={{ native: true }}
                        sx={commonSelectStyle}
                    >
                        <option value="">{t("fields.select_time")}</option>
                        {timeOptions.map((option, index) => (
                            <option key={index} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ marginBottom: "16px" }}>
                    <TextField
                        fullWidth
                        select
                        label={t("fields.project")}
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value)}
                        required
                        SelectProps={{ native: true }}
                        sx={commonSelectStyle}
                    >
                        <option value="">{t("fields.select_project")}</option>
                        {projects.map((p, i) => (
                            <option key={i} value={p.id}>{p.title}</option>
                        ))}
                    </TextField>
                </Box>

                {selectedProjectObj && (
                    <>
                        <Box sx={{ marginBottom: "16px" }}>
                            <TextField
                                fullWidth
                                select
                                label={t("fields.phase")}
                                value={selectedPhase}
                                onChange={(e) => setSelectedPhase(e.target.value)}
                                SelectProps={{ native: true }}
                                sx={commonSelectStyle}
                            >
                                <option value="">{t("fields.select_phase")}</option>
                                {selectedProjectObj.phases?.map((ph) => (
                                    <option key={ph.id} value={ph.id}>
                                        {ph.title}
                                    </option>
                                ))}
                            </TextField>
                        </Box>

                        <Box sx={{ marginBottom: "16px" }}>
                            <TextField
                                fullWidth
                                select
                                label={t("fields.category")}
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                SelectProps={{ native: true }}
                                sx={commonSelectStyle}
                            >
                                <option value="">{t("fields.select_category")}</option>
                                {selectedProjectObj.categories?.map((cat) => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.title}
                                    </option>
                                ))}
                            </TextField>
                        </Box>

                        <Box sx={{ marginBottom: "16px" }}>
                            <TextField
                                fullWidth
                                select
                                label={t("fields.task")}
                                value={form.task_id}
                                onChange={(e) => setForm({ ...form, task_id: e.target.value })}
                                SelectProps={{ native: true }}
                                sx={commonSelectStyle}
                            >
                                <option value="">{t("fields.select_task")}</option>
                                {filteredTasks.map((task) => (
                                    <option key={task.id} value={task.id}>
                                        {task.title}
                                    </option>
                                ))}
                            </TextField>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ color: "#000" }}>
                    {t("actions.cancel")}
                </Button>
                <Button onClick={handleSubmit} sx={{ backgroundColor: "#004d40", color: "white", padding: '10px' }}>
                    {t("actions.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTimeLogDialogWithoutTaskId;
